import { PageContainer, Spinner } from "@shapr3d/frontend-lib";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";

import { OverlayProvider } from "../../components/Headers/components/ShareModal/components/MenuPortal";
import { ViewLinkList } from "../../components/ViewLinkList/ViewLinkList";
import type { UserID } from "../../hooks/useAccount";
import type { User } from "../../hooks/useComments";
import { useGuardPrivateCloudTeams } from "../../hooks/useGuardPrivateCloudTeams";
import { useGuardSyncOffTeams } from "../../hooks/useGuardSyncOffTeams";
import { SyncRequestClient } from "../../stores/requestClient";
import type { ProjectID, ViewLinkID } from "../../types";

import style from "./ViewLinkListPage.module.css";

export type Space = {
  id: string;
  spaceName?: string;
  spaceType: "private" | "team";
};

export type ProjectViewLink = {
  title: string;
  projectViewLinkId: ViewLinkID;
  projectId: ProjectID;
  space?: Space;
  uploadedAt: Date;
  uploadedByUserId?: UserID;
};

export type ProjectViewLinksResponseData = { projectViewLinks: ProjectViewLink[]; users: User[] };

export const projectViewLinksQueryKey = ["projectViewLinks"];

const useProjectViewLinks = (teamId: string | undefined) => {
  return useQuery<ProjectViewLinksResponseData>({
    queryKey: [...projectViewLinksQueryKey, teamId],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/project-view-links?teamId=${teamId}`,
      });

      return response.json<ProjectViewLinksResponseData>();
    },
    enabled: Boolean(teamId),
  });
};

function ViewLinkListPageComponent() {
  const isRedirecting = useGuardPrivateCloudTeams();
  useGuardSyncOffTeams({ enabled: !isRedirecting });

  const { teamID } = useParams();
  const { data, isLoading } = useProjectViewLinks(teamID);

  if (isLoading) {
    return (
      <div className={style.pageContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <OverlayProvider>
      <PageContainer title="Published versions">
        <ViewLinkList
          viewLinks={data?.projectViewLinks ?? []}
          users={data?.users ?? []}
          telemetryLocation="review_links"
        />
      </PageContainer>
    </OverlayProvider>
  );
}

export default function ViewLinkListPage() {
  return (
    <>
      <Helmet>
        <title>Shapr3D - Dashboard</title>
        <meta
          name="description"
          content="Review designs together. View models and add comments directly to your 3D design."
        />
      </Helmet>
      <ViewLinkListPageComponent />
    </>
  );
}
